/* ===================================================================
   useRequireCardToSend() -- the ONE card-at-send guard (Steve directive
   2026-07-08). Setup stays card-free; the CARD is required at the FIRST
   review-request SEND (the activation event). This hook is the single,
   reused guard for every place that can trigger that send.

   Access truth comes from useSubscriptionAccess (has_active_subscription +
   the operator/demo whitelist). A CARD-FREE owner (state === 'gated') is
   intercepted BEFORE the send fires and routed to /choose-plan with the
   send framing ("Start your 14-day free trial to send your first request").
   Whitelisted operator/demo and already-subscribed owners send normally
   (interceptSend returns false -> the caller proceeds).

   Consumers:
     - Requests page handleSend  -- the create/send choke point; shows the
       inline card step on that page at the first real send.
     - PostAuthLanding           -- routes a gated owner appropriately.
   =================================================================== */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionAccess } from './useSubscriptionAccess';
import { hasStripePublishableKey } from '../lib/stripe';

export interface RequireCardToSend {
  /* True only for a card-free owner who must add a card before sending. */
  gated: boolean;
  /* True when the card step can be captured INLINE on the send screen (owner is
     gated AND a Stripe publishable key is configured). When false but gated, the
     caller falls back to the hosted /choose-plan redirect (via interceptSend).
     This is what lets the inline flow ship dormant until the pk is set: no key
     -> today's working redirect; key present -> inline card step. */
  inlineAvailable: boolean;
  /* Legacy redirect guard. Call at the top of a send action. Returns true if it
     intercepted (routed to /choose-plan) -- the caller MUST abort its send.
     Returns false when the owner may send (whitelisted/subscribed) OR when the
     inline card step is available (the caller should show that instead). */
  interceptSend: () => boolean;
}

export function useRequireCardToSend(): RequireCardToSend {
  const navigate = useNavigate();
  const { state } = useSubscriptionAccess();
  // Only a resolved 'gated' state blocks. 'loading' does NOT intercept (we do
  // not want to bounce a subscribed owner mid-load); the backend 402 is the
  // fail-closed backstop if a truly card-free send slips through while loading.
  const gated = state === 'gated';
  const inlineAvailable = gated && hasStripePublishableKey;
  const interceptSend = useCallback((): boolean => {
    // When the inline card step is available, DON'T redirect -- the caller shows
    // the embedded card step on the same screen and fires the send on success.
    if (inlineAvailable) return false;
    if (gated) {
      navigate('/choose-plan?intent=send');
      return true;
    }
    return false;
  }, [gated, inlineAvailable, navigate]);
  return { gated, inlineAvailable, interceptSend };
}
